import React, { useEffect, useState, useRef } from 'react';
import { createPortal } from 'react-dom';
import { useLayoutVariant } from '../../hooks/useLayoutVariant';

interface SpotlightRect {
  top: number;
  left: number;
  width: number;
  height: number;
}

const STEP_SPOTLIGHT_MAP: Record<string, string[]> = {
  priceChart: ['price-chart'],
  group1Analysis: ['metric-group-1'],
  group2Analysis: ['metric-group-2'],
  group3Analysis: ['metric-group-3'],
  group4Analysis: ['metric-group-4'],
};

const PADDING = 8;

interface AnalysisSpotlightProps {
  step: string;
}

/**
 * Creates a spotlight effect during avatar analysis dialogue.
 * Dims the entire page except the relevant game section (chart or metric row).
 * On mobile, auto-scrolls the target into view.
 */
export const AnalysisSpotlight: React.FC<AnalysisSpotlightProps> = ({ step }) => {
  const [spotlightRects, setSpotlightRects] = useState<SpotlightRect[]>([]);
  const prevStepRef = useRef(step);
  const layoutVariant = useLayoutVariant();

  const targetIds = STEP_SPOTLIGHT_MAP[step];

  // Auto-scroll on single-column layouts (mobile + streamer mode on desktop)
  useEffect(() => {
    if (!targetIds || step === prevStepRef.current) {
      prevStepRef.current = step;
      return;
    }
    prevStepRef.current = step;

    if (layoutVariant.type !== 'mobile') return;

    // Delay to let MobileMetricCards render the correct tier
    const rafId = requestAnimationFrame(() => {
      const el = document.querySelector(`[data-spotlight-id="${targetIds[0]}"]`);
      if (el) {
        const rect = el.getBoundingClientRect();
        const targetY = window.scrollY + rect.top - window.innerHeight * 0.15;
        window.scrollTo({ top: targetY, behavior: 'smooth' });
      }
    });

    return () => cancelAnimationFrame(rafId);
  }, [step, targetIds, layoutVariant.type]);

  useEffect(() => {
    if (!targetIds) return;

    let rafId: number;
    let observer: MutationObserver | null = null;

    const updateSpotlights = () => {
      const rects: SpotlightRect[] = [];

      targetIds.forEach(id => {
        const element = document.querySelector(`[data-spotlight-id="${id}"]`);
        if (element) {
          const rect = element.getBoundingClientRect();
          if (rect.width > 0 && rect.height > 0) {
            rects.push({
              top: rect.top - PADDING,
              left: rect.left - PADDING,
              width: rect.width + PADDING * 2,
              height: rect.height + PADDING * 2,
            });
          }
        }
      });

      setSpotlightRects(rects);

      // If we didn't find all targets, observe DOM for changes
      if (rects.length < targetIds.length && !observer) {
        observer = new MutationObserver(() => {
          cancelAnimationFrame(rafId);
          rafId = requestAnimationFrame(updateSpotlights);
        });
        observer.observe(document.body, { childList: true, subtree: true });
      }

      // Found everything — disconnect observer
      if (rects.length === targetIds.length && observer) {
        observer.disconnect();
        observer = null;
      }
    };

    // Initial attempt after a rAF to let sibling components flush to DOM
    rafId = requestAnimationFrame(updateSpotlights);

    window.addEventListener('resize', updateSpotlights);
    window.addEventListener('scroll', updateSpotlights, true);

    return () => {
      cancelAnimationFrame(rafId);
      observer?.disconnect();
      window.removeEventListener('resize', updateSpotlights);
      window.removeEventListener('scroll', updateSpotlights, true);
    };
  }, [targetIds]);

  // Don't render for intro or unknown steps
  if (!targetIds || spotlightRects.length === 0) {
    return null;
  }

  return createPortal(
    <div className="fixed inset-0 pointer-events-none z-[2147483000]">
      <svg width="100%" height="100%" className="absolute inset-0">
        <defs>
          <mask id="analysis-spotlight-mask">
            <rect x="0" y="0" width="100%" height="100%" fill="white" />
            {spotlightRects.map((rect, index) => (
              <rect
                key={index}
                x={rect.left}
                y={rect.top}
                width={rect.width}
                height={rect.height}
                fill="black"
                rx="8"
              />
            ))}
          </mask>
        </defs>
        <rect
          x="0"
          y="0"
          width="100%"
          height="100%"
          fill="rgba(0, 0, 0, 0.6)"
          mask="url(#analysis-spotlight-mask)"
        />
      </svg>

      {spotlightRects.map((rect, index) => (
        <div
          key={index}
          className="absolute border-2 border-amber-500 rounded-lg pointer-events-none"
          style={{
            top: `${rect.top}px`,
            left: `${rect.left}px`,
            width: `${rect.width}px`,
            height: `${rect.height}px`,
          }}
        />
      ))}
    </div>,
    document.body
  );
};

export default AnalysisSpotlight;
